import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { IUserExistsResponse } from '../../../auth/interfaces/user-exists-reponse.interface';
import { VerifiedEmail } from '../../../auth/model/email-verification-token.model';
import { TokenService } from '../../../auth/services/token.service';
import { AppConfig } from '../../../core/services/app-config.service';
import {
  IChangePasswordForm,
  ICodeVerificationForm,
  IEmailVerificationForm,
  ILoginForm,
  IRegistrationForm,
  ISendEmailForm,
  IUserInfo,
  IVerifyCodeForm,
} from '../../interfaces/auth.interface';
import { IsUserDisabled } from '../../../auth/interfaces/user-is-enabled';
import { WarningStoreService } from '../../../auth/services/warning-store.service';

@Injectable({
  providedIn: 'root',
})
export class AuthApiService {
  constructor(
    private appConfig: AppConfig,
    private http: HttpClient,
    private router: Router,
    private config: AppConfig,
    private tokenService: TokenService,
    private warningStore: WarningStoreService,
  ) {
  }

  private static getOptions(): { headers: HttpHeaders } {
    return {
      headers: new HttpHeaders().append('Content-Type', 'application/x-www-form-urlencoded'),
    };
  }

  private static getLoginOptions(): { headers: HttpHeaders } {
    return {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
    };
  }

  public userExists(email: string): Observable<IUserExistsResponse> {
    return this.http.post<IUserExistsResponse>(`${this.appConfig.config.authUrl}/userExists`, email);
  }

  public login(data: ILoginForm): Observable<any> {
    const { oauth2 } = this.appConfig.config;

    var body;
    if (data.totp) {
      body = {
        'client_id': oauth2.clientId,
        'username': data.email,
        'password': data.password,
        'otp': data.totp,
      };
    } else {
      body = {
        'client_id': oauth2.clientId,
        'username': data.email,
        'password': data.password,
      };
    }

    return this.http
      .post(data.totp ? `${oauth2.issuer}/login/password-otp` : `${oauth2.issuer}/login/password`,
        body, AuthApiService.getLoginOptions())
      .pipe(tap((res) => this.setInternalTokens(res)));
  }

  public register(data: IRegistrationForm): Observable<any> {
    if (this.appConfig.config.features.activateCouponEnabled === false) {
      data.referralCode = null;
    }
    return this.http.post(`${this.appConfig.config.authUrl}/register`, data);
  }

  public verifyEmail(data: IEmailVerificationForm): Observable<VerifiedEmail> {
    return this.http.post(`${this.appConfig.config.authUrl}/verifyEmail`, data).pipe(
      catchError(
        (error): Observable<any> => {
          if (error.status === 404) {
            throw ['VERIFICATION_CODE_INVALID'];
          }
          throw [];
        },
      ),
      switchMap(() => this.refreshToken()),
    );
  }

  public verifyEmailToken(data: IEmailVerificationForm): Observable<VerifiedEmail> {
    return this.http.post<VerifiedEmail>(`${this.appConfig.config.authUrl}/email/verification-token`, data).pipe(
      catchError(
        (error): Observable<any> => {
          if (error.status === 404) {
            throw ['VERIFICATION_CODE_INVALID'];
          }
          throw [];
        },
      ),
      switchMap((value) =>
        this.refreshToken().pipe(
          map(() => value),
          catchError(() => of(value)),
        ),
      ),
    );
  }

  public resendEmailVerification(userInfo: IUserInfo): Observable<any> {
    return this.http.post(`${this.appConfig.config.authUrl}/resendVerificationEmail`, { userInfo });
  }

  public discontinueEmailVerification(path: string): Observable<any> {
    const url = path ? path : '/auth/login';
    return of(true).pipe(
      tap(() => {
        this.tokenService.clearLocalStorageItems();
        this.router.navigate([url], { queryParamsHandling: 'preserve' });
      }),
    );
  }

  public sendEmail(data: ISendEmailForm): Observable<any> {
    return this.http.post(`${this.appConfig.config.authUrl}/initPasswordChange`, data);
  }

  public verifyCode(data: IVerifyCodeForm): Observable<any> {
    return this.http.post(`${this.appConfig.config.authUrl}/verificationCodeValid`, data).pipe(
      catchError(
        (error): Observable<any> => {
          this.throwValidError(error);
          throw [];
        },
      ),
    );
  }

  public changePassword(data: IChangePasswordForm): Observable<any> {
    return this.http.post(`${this.appConfig.config.authUrl}/changePassword`, data).pipe(
      catchError(
        (error): Observable<any> => {
          this.throwValidError(error);
          throw [];
        },
      ),
    );
  }

  private throwValidError(error: HttpErrorResponse): void {
    if (error.status === 404) {
      throw ['VERIFICATION_CODE_INVALID'];
    }
    if (error.status === 409) {
      throw ['VERIFICATION_CODE_MAX_TRY_LIMIT_EXCEEDED'];
    }
  }

  public logout(): Observable<any> {
    return this.http.post(`${this.appConfig.config.apiUrl}/auth/logout`, {});
  }

  public loggedIn(): Observable<any> {
    return this.http.put(`${this.appConfig.config.apiUrl}/auth/logged-in`, {});
  }

  public isDisabled(email: string): Observable<IsUserDisabled> {
    const data = { params: new HttpParams().set('emailAddress', email) };
    return this.http.get<IsUserDisabled>(`${this.appConfig.config.authUrl}/tooManyRequests?`, data);
  }

  public refreshToken(): Observable<any> {
    const refreshToken = this.tokenService.getRefreshToken();
    const formData = new URLSearchParams();
    const { oauth2 } = this.appConfig.config;

    formData.set('refresh_token', refreshToken);
    formData.set('client_id', oauth2.clientId);
    formData.set('grant_type', 'refresh_token');
    formData.set('scope', oauth2.scope);

    return this.http
      .post(`${oauth2.issuer}/protocol/openid-connect/token`, formData.toString(), AuthApiService.getOptions())
      .pipe(tap((res) => this.setInternalTokens(res)));
  }

  public setInternalTokens(keycloakResponse): void {
    this.tokenService.setToken(keycloakResponse.access_token);
    this.tokenService.setRefreshToken(keycloakResponse.refresh_token);
  }

  public registerEmail(data: IRegistrationForm): Observable<HttpResponse<string>> {
    if (this.appConfig.config.features.activateCouponEnabled === false) {
      data.referralCode = null;
    }
    return this.http.post(`${this.appConfig.config.authUrl}/register/addEmail`, data, {
      observe: 'response',
      responseType: 'text',
    });
  }

  public resendVerificationEmail(data: IUserInfo): Observable<any> {
    return this.http.post(`${this.appConfig.config.authUrl}/register/resendVerificationEmail`, data, {
      observe: 'response', responseType: 'text',
    });
  }

  public verifyEmailCode(data: ICodeVerificationForm, token?: string): Observable<HttpResponse<any>> {
    let options;
    if (token) {
      options = {
        observe: 'response',
        headers: { Auth: token },
      };
    } else {
      options = {
        observe: 'response',
      };
    }
    return this.http.post(`${this.appConfig.config.authUrl}/register/verifyEmail`, data, options)
      .pipe(
        catchError(
          (error): Observable<any> => {
            if (error.status === 400) {
              throw 'VERIFICATION_CODE_INVALID';
            }
            throw [];
          },
        ),
      );
  }

  public addPhone(data: IRegistrationForm, token: string): Observable<any> {
    return this.http.post(`${this.appConfig.config.authUrl}/register/addPhone`, data, {
      observe: 'response', responseType: 'text',
      headers: { Auth: token },
    });
  }

  public resendVerificationSms(data: IUserInfo, token: string): Observable<any> {
    return this.http.post(`${this.appConfig.config.authUrl}/register/resendVerificationSms`, data, {
      observe: 'response', responseType: 'text',
      headers: { Auth: token },
    });
  }

  public verifyPhoneCode(data: ICodeVerificationForm, token: string): Observable<any> {
    return this.http
      .post(`${this.appConfig.config.authUrl}/register/verifyPhone`, data, {
        observe: 'response', responseType: 'text',
        headers: { Auth: token },

      })
      .pipe(
        catchError(
          (error): Observable<any> => {
            if (error.status === 400) {
              throw ['VERIFICATION_CODE_INVALID'];
            }
            throw [];
          },
        ),
      );
  }

  public addPasswordAndSave(data: IRegistrationForm, token: string): Observable<any> {
    return this.http.post<string>(`${this.appConfig.config.authUrl}/register/save`, data, {
      observe: 'response',
      headers: { Auth: token },
    });
  }


  getOtpQrCode(data: ILoginForm): Observable<Blob> {
    const { oauth2 } = this.appConfig.config;
    var body = {
      'client_id': oauth2.clientId,
      'username': data.email,
      'password': data.password,
    };

    return this.http.post(`${oauth2.issuer}/totp/mfa-start`,
      body,
      { responseType: 'blob' });
  }

  registerTotp(data: ILoginForm): Observable<boolean> {
    const { oauth2 } = this.appConfig.config;
    var body = {
      'client_id': oauth2.clientId,
      'username': data.email,
      'password': data.password,
      'otp': data.totp,
    };
    return this.http.post<boolean>(`${oauth2.issuer}/totp/mfa-finish`, body);
  }

  public revertImpersonation() {
    var headers = new HttpHeaders();
    headers = headers.append('Content-Type', 'application/json');
    headers = headers.append('Authorization', 'Bearer ' + this.tokenService.getToken());

    const options = {
      headers: headers,
    };

    return this.http
      .post(
        `${this.config.config.oauth2.issuer}/login/impersonate/revert`, null, options,
      )
      .pipe(
        tap(() => {
          this.warningStore.setState({
            automaticLogout: false,
            showRedBorder: false,
          });
        }),
        tap((response: { access_token: string, refresh_token: string }) => {
          this.tokenService.setToken(response.access_token);
          this.tokenService.setRefreshToken(response.refresh_token);
          window.location.assign('/dashboard');
        }),
      );
  }

}
